import { useCallback, useEffect, useRef } from 'react';

interface PerformanceMetrics {
  loadTime: number;
  renderTime: number;
  memoryUsage?: number;
  connectionType?: string;
}

interface PerformanceThresholds {
  loadTime: number; // ms
  renderTime: number; // ms
  memoryUsage: number; // MB
}

interface UsePerformanceMonitoringOptions {
  enabled?: boolean;
  thresholds?: Partial<PerformanceThresholds>;
  logToConsole?: boolean;
}

const DEFAULT_THRESHOLDS: PerformanceThresholds = {
  loadTime: 3000, // 3 seconds
  renderTime: 100, // 100ms
  memoryUsage: 50, // 50MB
};

export const usePerformanceMonitoring = (options: UsePerformanceMonitoringOptions = {}) => {
  const {
    enabled = true,
    thresholds = {},
    logToConsole = false,
  } = options;

  const finalThresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };
  const renderStartTime = useRef<number | null>(null);
  const metricsHistory = useRef<PerformanceMetrics[]>([]);

  // Start render timing
  const startRenderTiming = useCallback(() => {
    if (!enabled) return;
    renderStartTime.current = performance.now();
  }, [enabled]);

  // Measure render time
  const measureRenderTime = useCallback((componentName?: string) => {
    if (!enabled || renderStartTime.current === null) return;
    
    const renderTime = performance.now() - renderStartTime.current;
    renderStartTime.current = null;

    const metrics: PerformanceMetrics = {
      loadTime: 0, // Will be set by page load metrics
      renderTime,
      memoryUsage: getMemoryUsage(),
      connectionType: getConnectionType(),
    };

    metricsHistory.current.push(metrics);

    // Check thresholds
    if (renderTime > finalThresholds.renderTime) {
      const message = `Slow render detected: ${componentName || 'Unknown'} took ${renderTime.toFixed(2)}ms`;
      
      if (logToConsole) {
        console.warn(message);
      }
    }

    return renderTime;
  }, [enabled, finalThresholds, logToConsole]);

  // Measure API call performance
  const measureApiCall = useCallback((url: string, duration: number, success: boolean) => {
    if (!enabled) return;

    const message = `API call ${success ? 'completed' : 'failed'}: ${url} (${duration}ms)`;
    
    if (logToConsole) {
        // Only log in development when explicitly enabled
      }
    

  }, [enabled, logToConsole]);

  // Get performance summary
  const getPerformanceSummary = useCallback(() => {
    if (!enabled || metricsHistory.current.length === 0) return null;

    const metrics = metricsHistory.current;
    const avgLoadTime = metrics.reduce((sum, m) => sum + m.loadTime, 0) / metrics.length;
    const avgRenderTime = metrics.reduce((sum, m) => sum + m.renderTime, 0) / metrics.length;
    const avgMemoryUsage = metrics.reduce((sum, m) => sum + (m.memoryUsage || 0), 0) / metrics.length;

    return {
      avgLoadTime,
      avgRenderTime,
      avgMemoryUsage,
      totalMeasurements: metrics.length,
    };
  }, [enabled]);

  // Monitor page load performance
  useEffect(() => {
    if (!enabled) return;

    const handleLoad = () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        const loadTime = navigation.loadEventEnd - navigation.fetchStart;
        
        if (loadTime > finalThresholds.loadTime) {
          const message = `Slow page load detected: ${loadTime.toFixed(2)}ms`;
          
          if (logToConsole) {
            console.warn(message);
          }
          

        }
      }
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, [enabled, finalThresholds, logToConsole]);

  return {
    startRenderTiming,
    measureRenderTime,
    measureApiCall,
    getPerformanceSummary,
  };
};

// Utility functions
function getMemoryUsage(): number | undefined {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    return memory.usedJSHeapSize / 1024 / 1024; // Convert to MB
  }
  return undefined;
}

function getConnectionType(): string | undefined {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    return connection.effectiveType || connection.type;
  }
  return undefined;
}

// Hook for API performance monitoring
export const useApiPerformanceMonitoring = () => {
  const { measureApiCall } = usePerformanceMonitoring();

  const wrapApiCall = useCallback(async <T extends any>(
    apiCall: () => Promise<T>,
    url: string
  ): Promise<T> => {
    const startTime = Date.now();
    let success = false;
    
    try {
      const result = await apiCall();
      success = true;
      return result;
    } catch (error) {
      throw error;
    } finally {
      const duration = Date.now() - startTime;
      measureApiCall(url, duration, success);
    }
  }, [measureApiCall]);

  return { wrapApiCall };
};